import {useState, useEffect} from 'react';
import {validateActivityDuration} from '@/services/activities';
import {getFormattedDateString} from '@/utils/commons';
import {
  ACTIVITY_TYPE,
  ACTIVITY_TYPE_TO_VALUE,
  ACTIVITY_TYPES_ENUM,
  isVideoActivityType,
  isFreeKickActivityType,
  isBackgammonActivityType,
  isDurationValidationSkipped
} from '@/types/Activity';
import {ALLOWED_ACTIVITY_SEGMENT_MARKETS} from '@/utils/constants/globalConstants';
import {useAppSelector, useAppDispatch} from '@/store';
import {useNavigate} from 'react-router-dom';
import {ROUTES} from '@/utils/constants/routesConstants';
import {Constants} from '@/utils/constants/commonConstants';
import {errorNotificationDuration} from '@/utils/constants/activityConstants';
import {setToastNotifications} from '@/store/slices/commonSlice';

/** Map UI `activityType` to API `name` for `activities/validate` (handles enum strings e.g. SURVEY, WATCH_VIDEO, GAME_FREE_KICK). */
const resolveActivityTypeForDurationValidate = (activityType) => {
  const mapped = ACTIVITY_TYPE_TO_VALUE[activityType];
  if (mapped) {
    return mapped;
  }
  if (activityType === ACTIVITY_TYPES_ENUM.SURVEY) {
    return ACTIVITY_TYPES_ENUM.SURVEY;
  }
  if (activityType === ACTIVITY_TYPES_ENUM.WATCH_VIDEO) {
    return ACTIVITY_TYPES_ENUM.WATCH_VIDEO;
  }
  if (activityType === ACTIVITY_TYPES_ENUM.GAME_FREE_KICK) {
    return ACTIVITY_TYPES_ENUM.GAME_FREE_KICK;
  }
  if (isVideoActivityType(activityType)) {
    return ACTIVITY_TYPES_ENUM.WATCH_VIDEO;
  }
  if (isFreeKickActivityType(activityType)) {
    return ACTIVITY_TYPES_ENUM.GAME_FREE_KICK;
  }
  if (isBackgammonActivityType(activityType)) {
    return ACTIVITY_TYPES_ENUM.GAME_BACKGAMMON;
  }
  return '';
};

export const useActivityValidation = () => {
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({});
  const [validationResponse, setValidationResponse] = useState(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const countryCode = useAppSelector((state) => state.countryCode).countryCode;
  const allowSameDateActivity =
    ALLOWED_ACTIVITY_SEGMENT_MARKETS.includes(countryCode);

  // Reset validation state when component unmounts
  useEffect(() => {
    return () => {
      resetValidation();
    };
  }, []);

  const createValidationPayload = (
    activityType,
    activityName,
    startDate,
    endDate,
    orderType
  ) => {
    const activityTypeValue =
      resolveActivityTypeForDurationValidate(activityType);
    return {
      name: activityTypeValue,
      title: activityName,
      activeStartDate: getFormattedDateString(startDate),
      activeEndDate: getFormattedDateString(endDate),
      ...(activityType === ACTIVITY_TYPE.POINTS_ON_ORDER_DELIVERY && {
        orderType
      })
    };
  };

  const handleActivityDurationValidation = async (formData) => {
    try {
      // Skip duration validation for activity types that do not support the API
      if (isDurationValidationSkipped(formData.activityType)) {
        return true;
      }

      const validationPayload = createValidationPayload(
        formData.activityType,
        formData.activityName,
        formData.startDate,
        formData.endDate,
        formData.orderType
      );
      const uuid = formData?.uuid ?? null;
      const response = await validateActivityDuration({
        ...validationPayload,
        ...(formData.id && uuid == null && {id: formData.id}),
        ...(uuid && {uuid: uuid})
      });
      setValidationResponse(response);

      // Only validate for countries that are not in the allowed list for same date activities
      if (response?.success === false && !allowSameDateActivity) {
        setShowToast(true);
        setToastMessage({
          title: `${formData.activityType} already created`,
          subTitle:
            'The activity type cannot be created multiple times with same attributes.',
          buttonText: 'View Activity'
        });
        return false; // Validation failed
      }

      // Validation passed - reset any existing toast state
      resetValidation();
      return true; // Validation passed
    } catch (error) {
      // Handle API errors gracefully
      if (error?.cause) {
        dispatch(
          setToastNotifications({
            show: true,
            message: error.cause,
            type: 'error',
            duration: errorNotificationDuration
          })
        );
      }
      setValidationResponse(null);
      setShowToast(false);
      return false; // Return false on error
    }
  };

  const handleToastClose = () => {
    setShowToast(false);
  };

  const resetValidation = () => {
    setShowToast(false);
    setToastMessage({});
    setValidationResponse(null);
  };

  const handleViewActivity = () => {
    if (validationResponse?.success === false && validationResponse?.data?.id) {
      // Reset the modal before navigating
      setShowToast(false);
      setToastMessage({});
      setValidationResponse(null);
      const queryParam = `?review=${Constants.ReviewPublished}`;

      // Navigate to the activity view
      navigate(
        `${ROUTES.ACTIVITY_VIEW.replace(':id', validationResponse?.data?.id)}${queryParam}`
      );
    }
  };

  const handlePointExpirationPublishError = (existingActivityId) => {
    setValidationResponse({
      success: false,
      data: existingActivityId ? {id: existingActivityId} : null
    });

    setShowToast(true);
    setToastMessage({
      title: 'Activity Type Already Created',
      subTitle:
        'An active Rolling Expiry Activity already exists. Delete it to create a new one.',
      buttonText: existingActivityId ? 'View Activity' : undefined
    });
  };

  return {
    showToast,
    toastMessage,
    validationResponse,
    handleActivityDurationValidation,
    handleToastClose,
    resetValidation,
    handleViewActivity,
    handlePointExpirationPublishError
  };
};
